import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { MobileLayout } from "@/components/Layout";
import { toast } from "sonner";
import { getRoleLabel, getRoleColor } from "@/lib/roles";
import {
  Building2,
  Users,
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  MapPin,
  FileText,
  Loader2,
  Pencil,
  Check,
  X,
  Shield,
  CircleDot,
  LogIn,
  Copy,
  CheckCircle,
  Key,
  Archive,
  RotateCcw,
  Trash2,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  actif: { label: "Actif", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  essai: { label: "Essai", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  suspendu: { label: "Suspendu", color: "bg-red-500/20 text-red-400 border-red-500/30" },
  "archivé": { label: "Archivé", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" },
};

const SuperAdminPage = () => {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", status: "essai" });
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState("companies");
  const [companyView, setCompanyView] = useState("active");
  const [archivedCompanies, setArchivedCompanies] = useState([]);

  // Credentials modal
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [copied, setCopied] = useState(false);

  // Email lookup tool
  const [emailLookup, setEmailLookup] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleEmailLookup = async () => {
    if (!emailLookup.trim()) return;
    setLookupLoading(true);
    setLookupResult(null);
    setDeleteConfirm("");
    try {
      const res = await api.post("/admin/search-user-by-email", { email: emailLookup.trim() });
      setLookupResult(res.data);
    } catch (e) {
      toast.error("Erreur de recherche");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleDeleteByEmail = async () => {
    if (deleteConfirm !== emailLookup.trim()) {
      toast.error("Retapez l'email pour confirmer");
      return;
    }
    setDeleting(true);
    try {
      const res = await api.post("/admin/delete-user-by-email", {
        email: emailLookup.trim(),
        confirm_email: deleteConfirm,
      });
      toast.success(res.data.message);
      setLookupResult(null);
      setEmailLookup("");
      setDeleteConfirm("");
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [cRes, arRes, uRes] = await Promise.all([
        api.get("/companies"),
        api.get("/companies?archived=true"),
        api.get("/users"),
      ]);
      setCompanies(cRes.data);
      setArchivedCompanies(arRes.data);
      setUsers(uRes.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (companyId, newStatus) => {
    try {
      await api.put(`/companies/${companyId}`, { status: newStatus });
      toast.success(`Statut mis a jour: ${STATUS_CONFIG[newStatus]?.label}`);
      loadData();
    } catch (e) {
      toast.error("Erreur lors de la mise a jour");
    }
  };

  const handleArchive = async (companyId) => {
    try {
      await api.put(`/companies/${companyId}/archive`);
      toast.success("Entreprise archivée");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'archivage");
    }
  };

  const handleRestore = async (companyId) => {
    try {
      await api.put(`/companies/${companyId}/restore`);
      toast.success("Entreprise restaurée");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la restauration");
    }
  };

  const startEditing = (company) => {
    setEditingId(company.id);
    setEditForm({
      name: company.name || "",
      email: company.email || "",
      address: company.address || "",
      phone: company.phone || "",
      siret: company.siret || "",
    });
  };

  const cancelEditing = () => { setEditingId(null); setEditForm({}); };

  const saveEditing = async (companyId) => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}`, editForm);
      toast.success("Entreprise mise a jour");
      setEditingId(null);
      loadData();
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email) return;
    setCreating(true);
    try {
      const res = await api.post("/companies", createForm);
      const data = res.data;
      toast.success("Entreprise creee");
      setShowCreate(false);
      setCreateForm({ name: "", email: "", status: "essai" });

      // Show credentials if admin user was auto-created
      if (data.admin_email && data.admin_password) {
        setCredentials({ email: data.admin_email, password: data.admin_password });
        setCopied(false);
        setShowCredentials(true);
      }

      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la creation");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyCredentials = async () => {
    const text = `Email: ${credentials.email}\nMot de passe: ${credentials.password}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Identifiants copies");
    setTimeout(() => setCopied(false), 3000);
  };

  const filtered = companies.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.status || "").toLowerCase().includes(search.toLowerCase())
  );

  const getUsersForCompany = (companyId) => users.filter((u) => u.company_id === companyId);

  const getAdminForCompany = (companyId) => users.find((u) => u.company_id === companyId && u.role === "admin_entreprise");

  const stats = {
    total: companies.length,
    actif: companies.filter((c) => c.status === "actif").length,
    essai: companies.filter((c) => c.status === "essai").length,
    suspendu: companies.filter((c) => c.status === "suspendu").length,
    archived: archivedCompanies.length,
    totalUsers: users.length,
  };

  if (loading) {
    return (
      <MobileLayout title="Plateforme">
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout title="Plateforme">
      <div className="px-4 lg:px-6 py-5 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-bold text-2xl tracking-wide">Plateforme</h2>
          <Button onClick={() => setShowCreate(true)} className="btn-primary h-9 text-xs gap-1.5" data-testid="sa-create-company">
            <Plus size={16} /> Entreprise
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
          <StatCard label="Entreprises" value={stats.total} icon={Building2} />
          <StatCard label="Actives" value={stats.actif} icon={CircleDot} accent="green" />
          <StatCard label="Essai" value={stats.essai} icon={CircleDot} accent="amber" />
          <StatCard label="Suspendues" value={stats.suspendu} icon={CircleDot} accent="red" />
          <StatCard label="Archivées" value={stats.archived} icon={Archive} accent="zinc" />
          <StatCard label="Utilisateurs" value={stats.totalUsers} icon={Users} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {[["companies", "Entreprises", Building2], ["users", "Utilisateurs", Users], ["tools", "Outils", Shield]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === key ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-zinc-300"}`} data-testid={`sa-tab-${key}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {/* Search (hidden on tools tab) */}
        {tab !== "tools" && (
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === "companies" ? "Rechercher une entreprise..." : "Rechercher un utilisateur..."} className="input-dark pl-9" data-testid="sa-search" />
        </div>
        )}

        {tab === "companies" ? (
          <>
            {/* Sub-tabs: Active / Archived */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setCompanyView("active")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${companyView === "active" ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" : "text-zinc-500 hover:text-zinc-300 border border-zinc-800"}`}
                data-testid="sa-view-active"
              >
                <Building2 size={14} /> Actives ({companies.length})
              </button>
              <button
                onClick={() => setCompanyView("archived")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${companyView === "archived" ? "bg-zinc-500/20 text-zinc-300 border border-zinc-500/30" : "text-zinc-500 hover:text-zinc-300 border border-zinc-800"}`}
                data-testid="sa-view-archived"
              >
                <Archive size={14} /> Archivées ({archivedCompanies.length})
              </button>
            </div>
            {companyView === "active" ? (
              <CompanyList
                companies={filtered}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
                editingId={editingId}
                editForm={editForm}
                setEditForm={setEditForm}
                startEditing={startEditing}
                cancelEditing={cancelEditing}
                saveEditing={saveEditing}
                saving={saving}
                handleStatusChange={handleStatusChange}
                getUsersForCompany={getUsersForCompany}
                getAdminForCompany={getAdminForCompany}
                onArchive={handleArchive}
              />
            ) : (
              <CompanyList
                companies={archivedCompanies.filter((c) =>
                  c.name.toLowerCase().includes(search.toLowerCase()) ||
                  c.email.toLowerCase().includes(search.toLowerCase())
                )}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
                editingId={null}
                editForm={{}}
                setEditForm={() => {}}
                startEditing={() => {}}
                cancelEditing={() => {}}
                saveEditing={() => {}}
                saving={false}
                handleStatusChange={() => {}}
                getUsersForCompany={getUsersForCompany}
                getAdminForCompany={getAdminForCompany}
                onRestore={handleRestore}
                isArchivedView
              />
            )}
          </>
        ) : tab === "users" ? (
          <UserList users={users.filter((u) => u.email.toLowerCase().includes(search.toLowerCase()) || (u.first_name || "").toLowerCase().includes(search.toLowerCase()) || (u.company_name || "").toLowerCase().includes(search.toLowerCase()))} onImpersonate={async (userId) => { const ok = await impersonate(userId); if (ok) navigate("/dashboard"); }} />
        ) : (
          /* ===== TOOLS TAB ===== */
          <div className="space-y-6">
            {/* Email lookup / duplicate cleanup */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4" data-testid="email-lookup-tool">
              <div>
                <h3 className="text-white font-bold text-base flex items-center gap-2">
                  <Search size={18} className="text-orange-500" />
                  Recherche et suppression par email
                </h3>
                <p className="text-zinc-500 text-xs mt-1">
                  Recherchez un utilisateur par email exact, puis supprimez-le si nécessaire (comptes doublons, erreurs de création).
                </p>
              </div>

              <div className="flex gap-2">
                <Input
                  value={emailLookup}
                  onChange={(e) => setEmailLookup(e.target.value)}
                  placeholder="email@exemple.com"
                  className="input-dark flex-1"
                  data-testid="email-lookup-input"
                  onKeyDown={(e) => e.key === "Enter" && handleEmailLookup()}
                />
                <Button
                  onClick={handleEmailLookup}
                  disabled={lookupLoading || !emailLookup.trim()}
                  className="btn-primary px-4"
                  data-testid="email-lookup-btn"
                >
                  {lookupLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                </Button>
              </div>

              {lookupResult && !lookupResult.found && (
                <div className="bg-zinc-800/50 rounded-lg p-4 text-center" data-testid="email-not-found">
                  <p className="text-zinc-400 text-sm">Aucun utilisateur trouvé avec cet email.</p>
                </div>
              )}

              {lookupResult && lookupResult.found && (
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3" data-testid="email-found-result">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-semibold">{lookupResult.user.first_name || "(sans nom)"}</p>
                      <p className="text-zinc-400 text-sm">{lookupResult.user.email}</p>
                      <div className="flex gap-3 mt-1.5 text-xs text-zinc-500">
                        <span>Rôle : <span className="text-zinc-300">{lookupResult.user.role}</span></span>
                        <span>Entreprise : <span className="text-zinc-300">{lookupResult.user.company_name || "—"}</span></span>
                        <span>ID : <span className="text-zinc-300 font-mono">{lookupResult.user.id?.slice(0, 8)}...</span></span>
                      </div>
                    </div>
                  </div>

                  {/* Delete section */}
                  <div className="border-t border-zinc-700 pt-3 space-y-2">
                    <p className="text-red-400 text-xs font-medium flex items-center gap-1.5">
                      <AlertTriangle size={12} />
                      Zone dangereuse — Retapez l{"'"}email pour confirmer la suppression
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        placeholder={`Retapez ${emailLookup.trim()} pour confirmer`}
                        className="input-dark flex-1 text-sm"
                        data-testid="email-delete-confirm"
                      />
                      <Button
                        onClick={handleDeleteByEmail}
                        disabled={deleting || deleteConfirm !== emailLookup.trim()}
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700 text-white px-4 gap-1.5"
                        data-testid="email-delete-btn"
                      >
                        {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Company Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="sa-create-modal">
              <h3 className="text-white font-bold text-lg">Nouvelle entreprise</h3>
              <p className="text-zinc-400 text-sm">Un compte admin_entreprise sera cree automatiquement</p>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm">Nom</Label>
                  <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="input-dark" required data-testid="sa-create-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm">Email</Label>
                  <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="input-dark" required data-testid="sa-create-email" />
                  <p className="text-zinc-500 text-xs">Cet email sera l{"'"}identifiant du compte admin de l{"'"}entreprise</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm">Statut</Label>
                  <select value={createForm.status} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })} className="select-dark w-full" data-testid="sa-create-status">
                    <option value="actif">Actif</option>
                    <option value="essai">Essai</option>
                    <option value="suspendu">Suspendu</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Annuler</Button>
                  <Button type="submit" disabled={creating} className="btn-primary flex-1" data-testid="sa-create-submit">
                    {creating ? <Loader2 size={16} className="animate-spin" /> : "Creer"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Credentials Modal */}
        {showCredentials && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCredentials(false)}>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="sa-credentials-modal">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle size={24} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Entreprise creee</h3>
                  <p className="text-zinc-400 text-sm">Compte admin_entreprise genere</p>
                </div>
              </div>

              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 space-y-3">
                <div className="space-y-1">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Email (identifiant)</p>
                  <p className="text-white font-mono text-sm break-all" data-testid="sa-cred-email">{credentials.email}</p>
                </div>
                <div className="border-t border-zinc-800 pt-3 space-y-1">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Mot de passe genere</p>
                  <div className="flex items-center gap-2">
                    <Key size={16} className="text-orange-500" />
                    <p className="text-orange-400 font-mono text-sm" data-testid="sa-cred-password">{credentials.password}</p>
                  </div>
                </div>
                <div className="border-t border-zinc-800 pt-3">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Role</p>
                  <p className="text-orange-400 text-sm font-medium">Admin Entreprise</p>
                </div>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                <p className="text-orange-300 text-sm text-center">Sauvegardez ces identifiants. Ils sont necessaires pour la connexion du client.</p>
              </div>

              <Button onClick={handleCopyCredentials} className={`w-full h-12 font-semibold transition-all ${copied ? "bg-green-600 hover:bg-green-600 text-white" : "btn-primary"}`} data-testid="sa-copy-credentials">
                {copied ? <><CheckCircle size={18} className="mr-2" /> Identifiants copies</> : <><Copy size={18} className="mr-2" /> Copier les identifiants</>}
              </Button>
              <Button onClick={() => setShowCredentials(false)} className="btn-secondary w-full">Fermer</Button>
            </div>
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

// ─── Sub components ───

const StatCard = ({ label, value, icon: Icon, accent }) => {
  const colors = { green: "text-green-400", amber: "text-amber-400", red: "text-red-400", zinc: "text-zinc-400" };
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={colors[accent] || "text-orange-500"} />
        <span className="text-zinc-500 text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-xl font-bold ${colors[accent] || "text-white"}`}>{value}</p>
    </div>
  );
};

const CompanyList = ({ companies, expandedId, setExpandedId, editingId, editForm, setEditForm, startEditing, cancelEditing, saveEditing, saving, handleStatusChange, getUsersForCompany, getAdminForCompany, onArchive, onRestore, isArchivedView }) => {
  if (companies.length === 0) {
    return <div className="empty-state py-10"><Building2 size={40} className="mx-auto mb-3 text-zinc-600" /><p className="text-zinc-500">{isArchivedView ? "Aucune entreprise archivée" : "Aucune entreprise"}</p></div>;
  }

  return (
    <div className="space-y-2">
      {companies.map((c) => {
        const isExpanded = expandedId === c.id;
        const isEditing = editingId === c.id;
        const compUsers = getUsersForCompany(c.id);
        const adminUser = getAdminForCompany(c.id);
        const statusCfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.actif;

        return (
          <div key={c.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`sa-company-${c.id}`}>
            {/* Header */}
            <div className="p-4 flex items-center gap-3 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : c.id)}>
              {c.logo_url ? (
                <img src={`${BACKEND_URL}${c.logo_url}`} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 size={18} className="text-zinc-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-white font-semibold truncate">{c.name}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded border font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                </div>
                <p className="text-zinc-500 text-sm truncate">{c.email}</p>
                {/* Show admin account info */}
                {adminUser && (
                  <p className="text-orange-400/70 text-xs mt-0.5">Admin: {adminUser.first_name || adminUser.email.split("@")[0]}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-zinc-600 text-xs">{compUsers.length} util.</span>
                {isExpanded ? <ChevronUp size={18} className="text-zinc-500" /> : <ChevronDown size={18} className="text-zinc-500" />}
              </div>
            </div>

            {/* Expanded */}
            {isExpanded && (
              <div className="border-t border-zinc-800">
                {/* Status controls */}
                <div className="px-4 py-3 bg-zinc-950/50 flex items-center gap-2 flex-wrap">
                  {!isArchivedView ? (
                    <>
                      <span className="text-zinc-500 text-xs uppercase tracking-wider mr-1">Statut:</span>
                      {Object.entries(STATUS_CONFIG).filter(([key]) => key !== "archivé").map(([key, cfg]) => (
                        <button
                          key={key}
                          onClick={() => handleStatusChange(c.id, key)}
                          className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                            c.status === key ? cfg.color : "text-zinc-500 border-zinc-700 hover:border-zinc-600"
                          }`}
                          data-testid={`sa-status-${key}-${c.id}`}
                        >
                          {cfg.label}
                        </button>
                      ))}
                      <button
                        onClick={() => onArchive && onArchive(c.id)}
                        className="text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors text-zinc-500 border-zinc-700 hover:border-zinc-500 hover:text-zinc-300 flex items-center gap-1"
                        data-testid={`sa-archive-${c.id}`}
                      >
                        <Archive size={12} /> Archiver
                      </button>
                      <div className="ml-auto">
                        {!isEditing ? (
                          <button onClick={() => startEditing(c)} className="text-zinc-400 hover:text-orange-400 transition-colors p-1" data-testid={`sa-edit-${c.id}`}>
                            <Pencil size={16} />
                          </button>
                        ) : (
                          <div className="flex gap-1">
                            <button onClick={() => saveEditing(c.id)} disabled={saving} className="text-green-400 hover:text-green-300 p-1" data-testid={`sa-save-${c.id}`}>
                              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            </button>
                            <button onClick={cancelEditing} className="text-zinc-400 hover:text-red-400 p-1"><X size={16} /></button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-zinc-500 text-xs uppercase tracking-wider mr-1">Archivée</span>
                      <button
                        onClick={() => onRestore && onRestore(c.id)}
                        className="text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors text-green-400 border-green-500/30 hover:bg-green-500/10 flex items-center gap-1"
                        data-testid={`sa-restore-${c.id}`}
                      >
                        <RotateCcw size={12} /> Restaurer
                      </button>
                    </>
                  )}
                </div>

                {/* Details / Edit form */}
                {isEditing ? (
                  <div className="px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-zinc-500 text-xs">Nom</Label>
                      <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="input-dark h-9 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-500 text-xs">Email</Label>
                      <Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="input-dark h-9 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-500 text-xs">Adresse</Label>
                      <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="input-dark h-9 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-500 text-xs">Telephone</Label>
                      <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="input-dark h-9 text-sm" />
                    </div>
                    <div className="space-y-1 lg:col-span-2">
                      <Label className="text-zinc-500 text-xs">SIRET</Label>
                      <Input value={editForm.siret} onChange={(e) => setEditForm({ ...editForm, siret: e.target.value })} className="input-dark h-9 text-sm" />
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                    {c.address && <InfoRow icon={MapPin} label="Adresse" value={c.address} />}
                    {c.phone && <InfoRow icon={Phone} label="Telephone" value={c.phone} />}
                    {c.siret && <InfoRow icon={FileText} label="SIRET" value={c.siret} />}
                    {!c.address && !c.phone && !c.siret && <p className="text-zinc-600 text-xs col-span-2">Aucune information complementaire</p>}
                  </div>
                )}

                {/* Admin account highlight */}
                {adminUser && (
                  <div className="px-4 py-3 border-t border-zinc-800/50 bg-orange-500/5">
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Compte administrateur</p>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <span className="text-orange-400 font-medium text-sm">{adminUser.first_name || adminUser.email.split("@")[0]}</span>
                        <span className="text-zinc-500 ml-2 text-xs">{adminUser.email}</span>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded font-medium bg-orange-500/20 text-orange-400">Admin Entreprise</span>
                    </div>
                  </div>
                )}

                {/* Company users (excluding admin) */}
                {compUsers.length > 0 && (
                  <div className="px-4 py-3 border-t border-zinc-800/50">
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Utilisateurs ({compUsers.length})</p>
                    <div className="space-y-1.5">
                      {compUsers.map((u) => (
                        <div key={u.id} className="flex items-center justify-between text-sm">
                          <div className="min-w-0">
                            <span className="text-zinc-300">{u.first_name || u.email.split("@")[0]}</span>
                            <span className="text-zinc-600 ml-2 text-xs">{u.email}</span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap ${getRoleColor(u.role)}`}>
                            {getRoleLabel(u.role)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {compUsers.length === 0 && !adminUser && (
                  <div className="px-4 py-3 border-t border-zinc-800/50">
                    <p className="text-zinc-600 text-xs text-center">Aucun utilisateur</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon size={14} className="text-zinc-600 mt-0.5 flex-shrink-0" />
    <div>
      <p className="text-zinc-500 text-xs">{label}</p>
      <p className="text-zinc-300">{value}</p>
    </div>
  </div>
);

const UserList = ({ users, onImpersonate }) => {
  if (users.length === 0) {
    return <div className="empty-state py-10"><Users size={40} className="mx-auto mb-3 text-zinc-600" /><p className="text-zinc-500">Aucun utilisateur</p></div>;
  }

  return (
    <div className="space-y-1.5" data-testid="sa-user-list">
      {users.map((u) => (
        <div key={u.id} className="bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 flex items-center justify-between" data-testid={`sa-user-${u.id}`}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-zinc-200 font-medium text-sm truncate">{u.first_name || u.email.split("@")[0]}</p>
              <span className={`text-xs px-2 py-0.5 rounded font-medium ${getRoleColor(u.role)}`}>{getRoleLabel(u.role)}</span>
            </div>
            <p className="text-zinc-500 text-xs truncate">{u.email}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 ml-3">
            {u.company_name && <p className="text-zinc-400 text-xs hidden lg:block">{u.company_name}</p>}
            {u.role !== "super_admin" && onImpersonate && (
              <button
                onClick={() => onImpersonate(u.id)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-amber-400 rounded-lg text-xs font-medium transition-colors"
                title={`Se connecter en tant que ${u.first_name || u.email}`}
                data-testid={`sa-impersonate-${u.id}`}
              >
                <LogIn size={14} />
                <span className="hidden lg:inline">Impersonner</span>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SuperAdminPage;
